import type { MemoryEntry } from '../stores/memory_entries'

// Pure helpers behind the Memory browser (MemoryBrowse + its columns):
// the scope/type/text filter, the type pill tone, and the short date the
// entry rows and metadata chips show. Kept out of the components so each
// rule has one tested home.

export type MemorySort = 'name' | 'updated'

// Sentinels for "no scope / type narrowing". Neither collides with a
// real value: scope slugs are repo names and `global`; types are the
// frontmatter vocabulary (feedback / project / reference / user).
export const ALL_SCOPES = 'all'
export const ALL_TYPES = 'all'

export interface MemoryFilter {
  scope: string
  type: string
  query: string
  sort: MemorySort
}

// Meaning colours from the design tokens: feedback waits on you (blue),
// project notes are factory work (amber), references are knowledge
// (mint), user notes are personal (violet). Unknown types stay neutral.
const TYPE_TONES: Record<string, string> = {
  feedback: 'bg-attention/15 text-attention',
  project: 'bg-factory/15 text-factory',
  reference: 'bg-brand-primary/15 text-brand-primary',
  user: 'bg-life/15 text-life',
}
const NEUTRAL_TONE = 'bg-surface-hover text-ink-muted'

/** Tailwind classes for an entry type's tinted pill. */
export function memoryTypeTone(type: string): string {
  return TYPE_TONES[type] ?? NEUTRAL_TONE
}

/** "7 Aug" this year, "7 Aug 2025" otherwise; '' for a missing or bad date. */
export function shortDate(iso: string | null | undefined, now: Date = new Date()): string {
  if (!iso) return ''
  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) return ''
  const sameYear = date.getFullYear() === now.getFullYear()
  return date.toLocaleDateString('en-GB', sameYear ? { day: 'numeric', month: 'short' } : { dateStyle: 'medium' })
}

/** Distinct entry types, alphabetical — the type filter chips. */
export function memoryEntryTypes(entries: MemoryEntry[]): string[] {
  return [...new Set(entries.map((e) => e.type).filter(Boolean))].toSorted()
}

/** Entries in one scope (or every scope for ALL_SCOPES). */
export function entriesInScope(entries: MemoryEntry[], scope: string): MemoryEntry[] {
  return scope === ALL_SCOPES ? entries : entries.filter((e) => e.project === scope)
}

function matchesQuery(entry: MemoryEntry, needle: string): boolean {
  return `${entry.name} ${entry.title} ${entry.description}`.toLowerCase().includes(needle)
}

/** Scope + type + text filter, then sort (newest first, or by name). */
export function filterMemoryEntries(entries: MemoryEntry[], filter: MemoryFilter): MemoryEntry[] {
  const needle = filter.query.trim().toLowerCase()
  const kept = entriesInScope(entries, filter.scope).filter(
    (e) => (filter.type === ALL_TYPES || e.type === filter.type) && (!needle || matchesQuery(e, needle)),
  )
  if (filter.sort === 'name') return kept.toSorted((a, b) => a.name.localeCompare(b.name))
  return kept.toSorted((a, b) => (b.updated_at || '').localeCompare(a.updated_at || ''))
}

/** Stable identity for an entry: the same name can live in two scopes. */
export function memoryEntryKey(entry: Pick<MemoryEntry, 'project' | 'name'>): string {
  return `${entry.project}/${entry.name}`
}

/** Route path of an entry's own page. */
export function memoryEntryPath(entry: Pick<MemoryEntry, 'project' | 'name'>): string {
  return `/memory/${encodeURIComponent(entry.project)}/${encodeURIComponent(entry.name)}`
}
